import * as React from "react";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import * as authText from "../../../ui/components/Auth/authTypography.style.css";
import type { User } from "../../lib/api/users";
import {
  formatVerificationExpiry,
  isEmailCodeExpired,
  isEmailSetupComplete,
} from "../../lib/auth/user-setup-progress";
import type { SetupTask } from "../../lib/auth/user-setup-progress";
import { getNextIncompleteTaskLabel } from "../../lib/auth/user-setup-progress";
import type { UserSetupSearch } from "../../lib/auth/user-setup-search";
import { EmailCodeEntry } from "../auth/EmailCodeEntry";
import { DevEmailCaptureLink } from "../dev/DevCaptureLinks";
import { SetupStepHeader } from "./SetupStepHeader";
import { useI18n } from "../../lib/i18n";

export function EmailSetupStep({
  user,
  search,
  displayEmail,
  verifyStatus,
  verifyMessage,
  resendLoading,
  onResendEmail,
  onManualCodeVerify,
  onClearVerification,
  onContinueToTask,
  nextTask,
  standalone = false,
}: {
  user: User | null | undefined;
  search: UserSetupSearch;
  displayEmail: string;
  verifyStatus: "idle" | "loading" | "success" | "error";
  verifyMessage: string | null;
  resendLoading: boolean;
  onResendEmail: () => void;
  onManualCodeVerify: (code: string) => void;
  onClearVerification: () => void;
  onContinueToTask?: (task: SetupTask) => void;
  nextTask?: SetupTask | null;
  standalone?: boolean;
}) {
  const { t, tx } = useI18n();
  const [codeEntryKey, setCodeEntryKey] = React.useState(0);

  const handleResendEmail = React.useCallback(() => {
    setCodeEntryKey((key) => key + 1);
    onResendEmail();
  }, [onResendEmail]);

  const panelProps = standalone
    ? {}
    : {
        role: "tabpanel" as const,
        id: "user-setup-panel-email",
        "aria-labelledby": "user-setup-tab-email",
      };
  const emailComplete =
    Boolean(user?.emailVerified) ||
    isEmailSetupComplete(user, search) ||
    verifyStatus === "success";
  const expired = isEmailCodeExpired(user);
  const expiryLabel = formatVerificationExpiry(user?.emailVerificationExpiresAt);
  const expiryText = expired
    ? t("account.setup.email.codeExpired")
    : expiryLabel
      ? t("account.setup.email.codeExpiresAt", { time: expiryLabel })
      : t("account.setup.email.codeExpiresIn24h");
  const resendLabel = resendLoading
    ? t("account.common.sending")
    : expired
      ? t("account.setup.email.sendNewEmail")
      : t("account.setup.email.resendEmail");

  if (emailComplete) {
    const email = user?.email ?? displayEmail;
    return (
      <div {...panelProps} className={authText.setupStepPanel}>
        <div className={authText.setupStepHeadingGroup}>
          <div
            className={authText.stepIconCircle}
            style={{ background: "#DEF7EC", color: "#057A55", border: "1px solid #BCF0DA" }}
          >
            <span style={{ fontSize: 22, lineHeight: 1 }} aria-hidden>
              ✓
            </span>
          </div>
          <SetupStepHeader
            title={tx("account.setup.email.verifiedTitle")}
            subtitle={
              <>
                {tx("account.setup.email.verifiedBody")}
                <br />
                <strong className={authText.strong}>{email}</strong>
              </>
            }
          />
        </div>
        {verifyStatus === "success" && verifyMessage && (
          <Callout variant="success">{verifyMessage}</Callout>
        )}
        {!standalone && nextTask && onContinueToTask && (
          <Button
            color="primary"
            variant="solid"
            fullWidth
            onClick={() => onContinueToTask(nextTask)}
          >
            {getNextIncompleteTaskLabel(nextTask)} →
          </Button>
        )}
      </div>
    );
  }

  return (
    <div {...panelProps} className={authText.setupStepPanel}>
      <SetupStepHeader
        title={tx("account.setup.email.title")}
        subtitle={
          verifyStatus === "loading" ? (
            t("account.setup.email.verifying")
          ) : (
            <>
              {tx("account.setup.email.sentCodePrefix")}{" "}
              <strong className={authText.strong}>{displayEmail}</strong>
            </>
          )
        }
      />
      <EmailCodeEntry
        key={codeEntryKey}
        email={displayEmail}
        onVerify={onManualCodeVerify}
        onCancel={onClearVerification}
        expandAsCta
      />
      <p className={authText.emailVerifyMetaRow}>
        <span>{expiryText}</span>
        <span className={authText.emailVerifyMetaSep} aria-hidden>
          ·
        </span>
        <button
          type="button"
          className={authText.emailVerifyMetaLink}
          disabled={resendLoading}
          onClick={handleResendEmail}
        >
          {resendLabel}
        </button>
      </p>
      <DevEmailCaptureLink />
    </div>
  );
}
